/**
 * Help (FAQ): the basics of the game in short questions and answers, for new players. Opens from
 * the page footer and Settings, or straight from a link (?screen=faq). There is no design for it:
 * it is built like Settings (title, cards, rows). Every number in the answers comes from the
 * config, so tuning keeps them right; the keys are the ones printed on the player's keyboard.
 */
import { ECONOMY, FIGHT, LEAGUES, ONLINE, PROGRESSION, SKILLS } from '@caged/shared';
import { Trans, useTranslation } from 'react-i18next';
import { DEFAULT_BINDINGS } from '../../input/bindings';
import { keyCapLabel, useKeyboardLayout } from '../../input/keyLabels';
import { DesktopHeader, TopBar } from '../components/Chrome';
import ui from '../components/ui.module.css';
import { formatNumber } from '../format';
import { useLayoutMode, useScrollTop } from '../hooks';
import '../ui.css';
import s from './FaqScreen.module.css';

const EMAIL = 'info@caged.gg';

/** The sections and their questions, in order (texts: locales/<lang>/faq.json). */
const SECTIONS = [
  { id: 'start', items: ['what', 'guest', 'opponents'] },
  { id: 'fight', items: ['controls', 'moves', 'signature', 'stamina', 'win'] },
  { id: 'progress', items: ['xp', 'level', 'skills', 'reset', 'weekly', 'rested'] },
  { id: 'leagues', items: ['how', 'join', 'when', 'away'] },
  { id: 'coins', items: ['what'] },
  { id: 'account', items: ['devices', 'settings'] },
] as const;

type Params = Record<string, string>;

/** What each answer quotes: numbers from the config, key caps from the player's keyboard. */
function useAnswerParams(): Record<string, Params> {
  const { t } = useTranslation();
  useKeyboardLayout();
  const n = (v: number) => formatNumber(v);
  const key = (codes: readonly string[]) => keyCapLabel(codes[0] ?? '');
  const { move, buttons } = DEFAULT_BINDINGS;
  const xp = PROGRESSION.xp;
  const bands = PROGRESSION.weeklyBands;
  const zeroAt = bands.findIndex((b) => b.factor === 0);
  const coins = ECONOMY.coins;
  return {
    'start.what': { rounds: n(FIGHT.rounds), seconds: n(FIGHT.roundSeconds) },
    'start.opponents': { seconds: n(ONLINE.matchmaking.botAfterSeconds) },
    'fight.controls': {
      move: [move.up, move.left, move.down, move.right].map(key).join(''),
      strike: key(buttons.strike),
      grapple: key(buttons.grapple),
      defend: key(buttons.defend),
      signature: t('hud.keys.space'),
    },
    'fight.win': { rounds: n(FIGHT.rounds) },
    'progress.xp': {
      win: n(xp.win),
      loss: n(xp.loss),
      draw: n(xp.draw),
      ko: n(xp.koBonus),
      firstWin: n(xp.firstWinOfDay),
      streakDays: n(xp.streakDays),
      streak: n(xp.streakReward),
    },
    'progress.level': { max: n(PROGRESSION.level.max) },
    'progress.skills': {
      skills: n(SKILLS.length),
      power: t('screens.skills.power.name'),
      speed: t('screens.skills.speed.name'),
      cardio: t('screens.skills.cardio.name'),
    },
    'progress.reset': { cost: n(ECONOMY.skillResetCost) },
    'progress.weekly': { full: n(bands[0]?.upTo ?? 0), zero: n(bands[zeroAt - 1]?.upTo ?? 0) },
    'progress.rested': { fights: n(PROGRESSION.rested.fights), factor: n(PROGRESSION.rested.factor) },
    'leagues.how': {
      top: t('screens.league.divisions.legend'),
      bottom: t('screens.league.divisions.rookie'),
      size: n(LEAGUES.seriesSize),
      days: n(LEAGUES.seasonDays),
    },
    'leagues.when': { minutes: n(LEAGUES.windowMinutes) },
    'coins.what': { win: n(coins.win), ko: n(coins.koWin), draw: n(coins.draw), loss: n(coins.loss) },
  };
}

function Faq({ desktop }: { desktop: boolean }) {
  const { t } = useTranslation();
  const params = useAnswerParams();
  return (
    <div className={s.stack}>
      <div className={s.head}>
        {desktop && <span className={ui.label}>{t('faq.kicker')}</span>}
        <h1 className={s.title}>{t('faq.title')}</h1>
        <p className={s.intro}>{t('faq.intro')}</p>
      </div>
      {SECTIONS.map((sec) => (
        <section key={sec.id} className={[ui.card, s.card].join(' ')} aria-labelledby={`faq-${sec.id}`}>
          <h2 id={`faq-${sec.id}`} className={[ui.label, s.cardTitle].join(' ')}>
            {t(`faq.${sec.id}.title`)}
          </h2>
          {sec.items.map((item) => {
            const p = params[`${sec.id}.${item}`];
            return (
              <details key={item} className={s.item}>
                <summary className={s.question}>
                  <span>{t(`faq.${sec.id}.${item}.q`, p)}</span>
                  <svg className={s.chevron} viewBox="0 0 12 12" aria-hidden="true">
                    <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </summary>
                <p className={s.answer}>{t(`faq.${sec.id}.${item}.a`, p)}</p>
              </details>
            );
          })}
        </section>
      ))}
      <p className={s.contact}>
        <Trans i18nKey="faq.contact" values={{ email: EMAIL }} components={{ a: <a href={`mailto:${EMAIL}`} /> }} />
      </p>
    </div>
  );
}

export function FaqScreen() {
  const { t } = useTranslation();
  const desktop = useLayoutMode() === 'desktop';
  useScrollTop();
  if (desktop) {
    return (
      <div className={['cg-ui', ui.screen].join(' ')}>
        <DesktopHeader />
        <main className={s.deskMain}>
          <Faq desktop />
        </main>
      </div>
    );
  }
  return (
    <div className={['cg-ui', ui.screen].join(' ')}>
      <div className={ui.column}>
        <TopBar title={t('faq.kicker')} heading={false} />
        <Faq desktop={false} />
      </div>
    </div>
  );
}
